import * as React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  FindAllAlertsQueryQueryVariables,
  FindAllAlertsQueryQuery
} from "../../../../schemaTypes";
import { newAlertSubscription } from "../../Alert/MyAlert";

export const findAllAlertsQuery = gql`
  query FindAllAlertsQuery(
    $page: Int!
    $pageSize: Int!
    $deliveryModes: [String!]
    $communityIds: [String!]
    $locationIds: [String!]
    $targets: [String!]
    $searchText: String
  ) {
    findAllAlerts(
      page: $page
      pageSize: $pageSize
      deliveryModes: $deliveryModes
      communityIds: $communityIds
      locationIds: $locationIds
      targets: $targets
      searchText: $searchText
    ) {
      data {
        id
        title
        update
        isSeen
        broadcast
        targetMode
        deliveryMode
        locationIds
        locationScopeAll
        communityIds
        communityScopeAll
        targetUserCount
        receivedCount
        queuedCount
        clickCount
        readCount
        unreadCount
        createdBy {
          id
          admin {
            firstname
            lastname
            email
          }
        }
        type
        typeId
        scheduledAt
        dispatchedAt
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

const dedupeAlertsById = <T extends { id: string }>(alerts: T[]): T[] => {
  const seen = new Set<string>();
  return alerts.filter((alert) => {
    if (seen.has(alert.id)) {
      return false;
    }

    seen.add(alert.id);
    return true;
  });
};

const dedupeTargetedAlerts = <T extends {
  title: string;
  update: string;
  createdAt: string;
  type?: string | null;
  typeId?: string | null;
  broadcast?: boolean | null;
}>(alerts: T[]): T[] => {
  const seenCampaigns = new Set<string>();

  return alerts.filter((alert) => {
    if (alert.broadcast) {
      return true;
    }

    const key = [
      alert.createdAt,
      alert.title,
      alert.update,
      alert.type || "",
      alert.typeId || "",
    ].join("|");

    if (seenCampaigns.has(key)) {
      return false;
    }

    seenCampaigns.add(key);
    return true;
  });
};

export function useFindAllAlerts(variables: FindAllAlertsQueryQueryVariables) {
  const { data, loading, fetchMore, subscribeToMore } = useQuery<FindAllAlertsQueryQuery, FindAllAlertsQueryQueryVariables>(
    findAllAlertsQuery,
    { variables }
  );

  React.useEffect(() => {
    if (!subscribeToMore) return;
    const unsubscribe = subscribeToMore({
      document: newAlertSubscription,
      variables,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        const merged = [
          ...(prev.findAllAlerts?.data || []),
          (subscriptionData.data as any).newAlert
        ];

        return {
          ...prev,
          findAllAlerts: {
            ...prev.findAllAlerts,
            data: dedupeTargetedAlerts(dedupeAlertsById(merged)),
          }
        };
      }
    });
    return () => unsubscribe();
  }, [subscribeToMore, variables]);

  const response = data?.findAllAlerts;
  const match = response?.data ?? [];
  const hasMoreAlert = Boolean(response && response.page < response.totalPages);

  const loadMore = () => {
    if (!response || !hasMoreAlert) {
      return;
    }

    fetchMore({
      variables: {
        ...variables,
        page: response.page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const merged = [
          ...(prev.findAllAlerts?.data || []),
          ...(fetchMoreResult.findAllAlerts?.data || []),
        ];

        return {
          ...prev,
          findAllAlerts: {
            ...fetchMoreResult.findAllAlerts,
            data: dedupeTargetedAlerts(dedupeAlertsById(merged)),
          },
        };
      }
    });
  };

  return {
    match,
    loading,
    loadMore,
    hasMoreAlert
  };
}